import type { XYChartPreviewSeries } from './xychart-compact-preview-model';
import { XYCHART_SERIES_TYPE } from './xychart-types';

const SPARK_PAD_TOP = 100;
const SPARK_PAD_BOTTOM = 60;
const SPARK_PAD_X = 24;

export interface XYChartSparkPoint {
  x: number;
  y: number;
}

export interface XYChartSparklineModel {
  zeroY: number;
  stepX: number;
  isBarChart: boolean;
  focusLinePath: string;
  focusAreaPath: string;
  lastPoint: XYChartSparkPoint | undefined;
  xPositionFor: (index: number) => number;
  yPositionFor: (value: number) => number;
}

export interface SelectXYChartSparklineModelInput {
  width: number;
  height: number;
  focusSeries: XYChartPreviewSeries;
  fadedSeries: XYChartPreviewSeries[];
}

export function selectXYChartSparklineModel({
  width,
  height,
  focusSeries,
  fadedSeries,
}: SelectXYChartSparklineModelInput): XYChartSparklineModel | null {
  const allValues = [focusSeries, ...fadedSeries].flatMap((series) => series.data);
  if (allValues.length === 0) return null;

  const rawMin = Math.min(...allValues);
  const rawMax = Math.max(...allValues);
  const valueMin = rawMin >= 0 ? Math.min(0, rawMin) : rawMin;
  const valueMax = rawMax === valueMin ? valueMin + 1 : rawMax;
  const valueRange = valueMax - valueMin;
  const usableHeight = Math.max(1, height - SPARK_PAD_TOP - SPARK_PAD_BOTTOM);
  const usableWidth = Math.max(1, width - SPARK_PAD_X * 2);
  const yPositionFor = (value: number): number =>
    SPARK_PAD_TOP + (1 - (value - valueMin) / valueRange) * usableHeight;
  const xCount = focusSeries.data.length;
  const stepX = xCount > 1 ? usableWidth / (xCount - 1) : usableWidth;
  const xPositionFor = (index: number): number =>
    SPARK_PAD_X + (xCount === 1 ? usableWidth / 2 : stepX * index);
  const focusPoints = focusSeries.data.map((value, index) => ({
    x: xPositionFor(index),
    y: yPositionFor(value),
  }));
  const focusLinePath = buildFocusLinePath({ points: focusPoints });

  return {
    zeroY: Math.min(Math.max(yPositionFor(0), SPARK_PAD_TOP), height - SPARK_PAD_BOTTOM),
    stepX,
    isBarChart: focusSeries.type === XYCHART_SERIES_TYPE.Bar,
    focusLinePath,
    focusAreaPath: buildFocusAreaPath({ focusLinePath, points: focusPoints, height }),
    lastPoint: focusPoints[focusPoints.length - 1],
    xPositionFor,
    yPositionFor,
  };
}

interface BuildFocusLinePathInput {
  points: XYChartSparkPoint[];
}

function buildFocusLinePath({ points }: BuildFocusLinePathInput): string {
  if (points.length === 0) return '';

  return (
    `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)} ` +
    points
      .slice(1)
      .map((point) => `L ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
      .join(' ')
  );
}

interface BuildFocusAreaPathInput {
  focusLinePath: string;
  points: XYChartSparkPoint[];
  height: number;
}

function buildFocusAreaPath({
  focusLinePath,
  points,
  height,
}: BuildFocusAreaPathInput): string {
  if (!focusLinePath || points.length <= 1) return '';

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  return `${focusLinePath} L ${lastPoint.x.toFixed(2)} ${height} L ${firstPoint.x.toFixed(
    2,
  )} ${height} Z`;
}

interface SelectFadedSparkSeriesKeyInput {
  series: XYChartPreviewSeries;
}

export function selectFadedSparkSeriesKey({
  series,
}: SelectFadedSparkSeriesKeyInput): string {
  if (series.type === XYCHART_SERIES_TYPE.Bar) {
    return `faded-bars-${series.seriesIndex}`;
  }

  return `faded-line-${series.seriesIndex}`;
}
